import {
  Button,
  CustomFilter,
  Form,
  Input,
  Modal,
  Paging,
  Select,
  Table,
  useBreadcrumbRoutes,
  useSearchParams,
  Wrapper,
} from '@maxtropy/components';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusOutlined } from '@ant-design/icons';
import {
  DeviationAlarmListItem,
  DeviationAlarmStatus,
  getTableListColumns,
  OptionsMap,
  useDeviationOptions,
} from './service';
import { Space } from 'antd';
import {
  apiV2DeviationRateAnalysisConfigRuleDeletePost,
  apiV2DeviationRateAnalysisConfigRuleDisablePost,
  apiV2DeviationRateAnalysisGetEnergyMediumsPost,
  apiV2DeviationRateAnalysisGetEnergyMediumTopologyNodePost,
  apiV2DeviationRateAnalysisGetRuleNamesPost,
  apiV2DeviationRateAnalysisPagePost,
  V2DeviationRateAnalysisPagePostRequest,
} from '@maxtropy/device-customer-apis-v2';

// 表单字段名（与API请求字段对应）
type FormValues = Partial<{
  ruleIds: number[];
  energyMediumTopologyId: number[];
  topologyNodeIds: number[];
  energyMediums: number[];
  status: number[];
  alarmLevel: number[];
}>;

type SearchParams = FormValues & {
  pageOffset: number;
  pageSize: number;
};

const DeviationAlarmConfig: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const breadcrumbRoutes = useBreadcrumbRoutes();

  const pagingInfo = useSearchParams<SearchParams>(50);
  const { searchParams, setTotalCount, finish, reset } = pagingInfo;
  const [loading, setLoading] = useState(true);

  const [tableData, setTableData] = useState<DeviationAlarmListItem[]>([]);
  const { loading: optionsLoading, optionsMap, fetchCascadeOptions } = useDeviationOptions();

  // 能源介质拓扑
  const energyMediumTopologyId = Form.useWatch('energyMediumTopologyId', form);
  // 拓扑节点
  const topologyNodeIds = Form.useWatch('topologyNodeIds', form);

  const fetch = () => {
    setLoading(true);
    const params: V2DeviationRateAnalysisPagePostRequest = {
      ruleIds: searchParams.ruleIds,
      energyMediumTopologyId: searchParams.energyMediumTopologyId,
      topologyNodeIds: searchParams.topologyNodeIds,
      energyMediums: searchParams.energyMediums,
      status: searchParams.status,
      alarmLevel: searchParams.alarmLevel,
      page: searchParams.pageOffset,
      size: searchParams.pageSize,
    };

    apiV2DeviationRateAnalysisPagePost(params)
      .then(res => {
        setTableData(res?.list || []);
        setTotalCount(res?.total || 0);
      })
      .finally(() => {
        !optionsLoading && setLoading(false);
      });
  };

  useEffect(() => {
    fetch();
  }, [searchParams]);

  // 单独领出来, 避免create时也要调用这个接口, 但是此时统一loading处理又被破坏了, 所以后续优化useDeviationOptions, 可选哪些字段要初始化时调用接口
  useEffect(() => {
    fetchCascadeOptions('ruleNames', () => apiV2DeviationRateAnalysisGetRuleNamesPost());
  }, []);

  useEffect(() => {
    // options响应比列表响应要慢的情况
    if (!optionsLoading && loading) {
      setLoading(false);
    }
  }, [optionsLoading]);

  const handlerSearch = (values: FormValues) => {
    finish({ ...values, pageOffset: 1, pageSize: searchParams.pageSize });
  };

  const handlerReset = () => {
    reset();
  };

  // 删除
  const handleDelete = (record: DeviationAlarmListItem) => {
    Modal.confirm({
      title: '确定删除？',
      content: '删除后不可恢复，你还要继续吗',
      okText: '继续',
      onOk: () => {
        apiV2DeviationRateAnalysisConfigRuleDeletePost({ id: record.id }).then(fetch);
      },
    });
  };

  // 禁用/启用
  const handleToggleStatus = (record: DeviationAlarmListItem) => {
    const isEnable = record.status === DeviationAlarmStatus.Enable;
    const title = isEnable ? '确定禁用？' : '确定启用？';
    const content = isEnable ? '禁用后将不再按照规则进行报警，你还要继续吗' : '启用后将按照规则进行报警，你还要继续吗';

    Modal.confirm({
      title,
      content,
      okText: '继续',
      onOk() {
        apiV2DeviationRateAnalysisConfigRuleDisablePost({ id: record.id }).then(fetch);
      },
    });
  };

  // 新增
  const handleCreate = () => {
    navigate(`/deviation/server/alarm/create`);
  };

  // 编辑
  const handleEdit = (record: DeviationAlarmListItem) => {
    navigate(`/deviation/server/alarm/edit/${record.id}`);
  };

  // 查看
  const handleView = (record: DeviationAlarmListItem) => {
    navigate(`/deviation/server/alarm/detail/${record.id}`);
  };

  // 能源介质拓扑change
  const handleEnergyMediumTopologyChange = (energyMediumTopologyId: number[]) => {
    fetchCascadeOptions('topologyNodeIds', () =>
      apiV2DeviationRateAnalysisGetEnergyMediumTopologyNodePost({ energyMediumTopologyId })
    );
    fetchCascadeOptions('energyMediums', () =>
      apiV2DeviationRateAnalysisGetEnergyMediumsPost({ energyMediumTopologyId, topologyNodeIds })
    );
    form.setFieldValue('topologyNodeIds', []);
    form.setFieldValue('energyMediums', []);
  };

  // 拓扑节点change
  const handleTopologyNodeChange = (topologyNodeIds: number[]) => {
    fetchCascadeOptions('energyMediums', () =>
      apiV2DeviationRateAnalysisGetEnergyMediumsPost({ energyMediumTopologyId, topologyNodeIds })
    );
    form.setFieldValue('energyMediums', []);
  };

  const filters = (
    <CustomFilter form={form} onFinish={handlerSearch} onReset={handlerReset}>
      <Form.Item name="ruleIds" label="规则名称">
        <Select placeholder="请选择" mode="multiple" options={optionsMap.ruleNames} optionFilterProp="label" />
      </Form.Item>
      <Form.Item name="energyMediumTopologyId" label="能源介质拓扑">
        <Select
          placeholder="请选择"
          mode="multiple"
          options={optionsMap.energyMediumTopologyId}
          optionFilterProp="label"
          onChange={handleEnergyMediumTopologyChange}
        />
      </Form.Item>
      <Form.Item name="topologyNodeIds" label="拓扑节点">
        <Select
          placeholder="请选择"
          mode="multiple"
          options={optionsMap.topologyNodeIds}
          optionFilterProp="label"
          onChange={handleTopologyNodeChange}
        />
      </Form.Item>
      <Form.Item name="energyMediums" label="能源介质">
        <Select placeholder="请选择" mode="multiple" options={optionsMap.energyMediums} optionFilterProp="label" />
      </Form.Item>
      <Form.Item name="status" label="状态">
        <Select placeholder="请选择" mode="multiple" options={optionsMap.status} optionFilterProp="label" />
      </Form.Item>
      <Form.Item name="alarmLevel" label="报警等级">
        <Select placeholder="请选择" mode="multiple" options={optionsMap.alarmLevel} optionFilterProp="label" />
      </Form.Item>
    </CustomFilter>
  );

  const columns = useMemo(() => getTableListColumns(), []);
  const buildColumns = [
    ...columns,
    {
      title: '操作',
      width: 200,
      fixed: 'right' as const,
      key: 'action',
      render: (_: any, record: DeviationAlarmListItem) => (
        <Space size={16}>
          <Button
            // 禁用才能删除
            disabled={record.status === DeviationAlarmStatus.Enable}
            type="link"
            onClick={() => handleDelete(record)}
          >
            删除
          </Button>
          <Button type="link" onClick={() => handleToggleStatus(record)}>
            {record.status === DeviationAlarmStatus.Enable ? '禁用' : '启用'}
          </Button>
          <Button type="link" onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Button type="link" onClick={() => handleView(record)}>
            查看
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Wrapper routes={breadcrumbRoutes?.routes} filters={filters} isLoading={loading}>
      <Button type="primary" icon={<PlusOutlined />} wrapStyle={{ marginBottom: 10 }} onClick={handleCreate}>
        新建规则
      </Button>
      <Table rowKey="id" columns={buildColumns} dataSource={tableData || []} scroll={{ x: 1300 }} />
      <Paging pagingInfo={pagingInfo} />
    </Wrapper>
  );
};

export default DeviationAlarmConfig;
